// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Crm.tsx
//  CRM — Relacionamento com Clientes
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Users, CheckCircle2, Clock, Eye, AlertCircle,
} from 'lucide-react';
import { useCrm } from '@/features/operacional/hooks/useCrm';
import type { FiltrosAtendimentos, OrdemAtendimentos } from '@/features/operacional/services/crmService';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { BarraFiltros } from '@/components/BarraFiltros';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import type { TamanhoPagina } from '@/hooks/usePaginacaoLocal';
import { rotuloCodigoNome } from '@/lib/consultas';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PRIORIDADE_CONFIG: Record<number, { label: string; className: string }> = {
  1: { label: 'Normal',  className: 'bg-blue-500/10 text-blue-500' },
  2: { label: 'Alta',    className: 'bg-orange-500/10 text-orange-500' },
  3: { label: 'Urgente', className: 'bg-red-500/10 text-red-500' },
};

const TODOS = '__todos__';

const Crm = () => {
  const navigate = useNavigate();

  const [filtros,       setFiltrosRaw]    = useState<FiltrosAtendimentos>({});
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);

  // Abre nos últimos 30 dias pela data de solicitação, mais recentes primeiro (v118).
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<OrdemAtendimentos>('solicitacao', true, () => setPagina(1));

  // Filtro novo volta para a 1ª página (a busca é no servidor, sobre todos os registros).
  const setFiltros = (f: (atual: FiltrosAtendimentos) => FiltrosAtendimentos) => {
    setFiltrosRaw(f);
    setPagina(1);
  };

  const { atendimentos, total, totalPaginas, contadores, isLoading, isFetching, deleteAtendimento } =
    useCrm({
      pagina, tamanhoPagina,
      filtros: {
        ...filtros,
        ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor,
        decrescente: ordenacao.decrescente,
      },
    });

  const filtrosAtivos = [
    !!filtros.busca, !!filtros.status, !!filtros.prioridadeId, !!filtros.clienteId,
  ].filter(Boolean).length;
  const limparFiltros = () => { setFiltros(() => ({})); periodo.setPeriodo('30dias'); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CRM — Relacionamento com Clientes</h1>
          <p className="text-muted-foreground mt-1">Registre e acompanhe os contatos com clientes</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/crm/novo')}>
          <Plus className="h-4 w-4" />Novo Atendimento
        </Button>
      </div>

      {/* KPIs — valem para o filtro inteiro, não só para a página */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{contadores.pendentes}</div>
            <p className="text-xs text-muted-foreground mt-1">Sem resultado esperado</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Concluídos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{contadores.concluidos}</div>
            <p className="text-xs text-muted-foreground mt-1">Resultado obtido</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />Urgentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{contadores.urgentes}</div>
            <p className="text-xs text-muted-foreground mt-1">Urgentes ainda pendentes</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />{filtrosAtivos > 0 ? 'Encontrados' : 'Total Geral'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{contadores.pendentes + contadores.concluidos}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {filtrosAtivos > 0 ? 'atendimentos no filtro' : 'atendimentos cadastrados'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader className="space-y-4">
          <CardTitle>Listagem de Atendimentos</CardTitle>
          <BarraFiltros
            busca={filtros.busca ?? ''}
            onBuscar={(b) => setFiltros((f) => ({ ...f, busca: b || undefined }))}
            placeholderBusca="Nº, assunto ou cliente..."
            filtrosAtivos={filtrosAtivos}
            onLimpar={limparFiltros}
          >
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={filtros.status ?? TODOS}
                onValueChange={(v) => setFiltros((f) => ({ ...f, status: v === TODOS ? undefined : v as FiltrosAtendimentos['status'] }))}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Prioridade</Label>
              <Select value={filtros.prioridadeId ? String(filtros.prioridadeId) : TODOS}
                onValueChange={(v) => setFiltros((f) => ({ ...f, prioridadeId: v === TODOS ? undefined : Number(v) }))}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todas</SelectItem>
                  {Object.entries(PRIORIDADE_CONFIG).map(([id, p]) => (
                    <SelectItem key={id} value={id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[260px] space-y-1">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <PessoaSearchCombobox value={filtros.clienteId} filter="cliente" placeholder="Todos os clientes"
                onChange={(id) => setFiltros((f) => ({ ...f, clienteId: id || undefined }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Solicitação</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <PeriodoFiltro estado={periodo} rotuloData="Solicitação" />
              </div>
            </div>
          </BarraFiltros>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                  <TableHead>Cliente</TableHead>
                  <CabecalhoOrdenavel coluna="assunto" ordenacao={ordenacao} decrescentePadrao={false}>Assunto</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="prioridade" ordenacao={ordenacao}>Prioridade</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="solicitacao" ordenacao={ordenacao}>Solicitação</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : atendimentos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo !== 'todos'
                        ? 'Nenhum atendimento encontrado neste período — amplie o período (ex.: Todo o período) para ver os anteriores'
                        : 'Nenhum atendimento encontrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  atendimentos.map((a) => {
                    const prio = PRIORIDADE_CONFIG[a.prioridadeId] ?? PRIORIDADE_CONFIG[1];
                    return (
                      <TableRow key={a.id} className="cursor-pointer" onClick={() => navigate(`/operacional/crm/${a.id}`)}>
                        <TableCell className="font-medium tabular-nums">{a.id}</TableCell>
                        <TableCell>
                          {a.clienteNome
                            ? rotuloCodigoNome(a.clienteId, a.clienteNome)
                            : <span className="italic text-muted-foreground">(cliente não encontrado)</span>}
                        </TableCell>
                        <TableCell>{a.sAssunto ?? '—'}</TableCell>
                        <TableCell>
                          <Badge className={prio.className}>{prio.label}</Badge>
                        </TableCell>
                        <TableCell>{new Date(a.dtSolicitacao).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</TableCell>
                        <TableCell>
                          <Badge className={a.isResultadoEsperado
                            ? 'bg-green-500/10 text-green-500'
                            : 'bg-blue-500/10 text-blue-500'}>
                            <span className="flex items-center gap-1">
                              {a.isResultadoEsperado
                                ? <><CheckCircle2 className="h-3 w-3" />Concluído</>
                                : <><Clock className="h-3 w-3" />Pendente</>}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" aria-label="Abrir atendimento"
                              onClick={() => navigate(`/operacional/crm/${a.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" aria-label="Excluir atendimento"
                              onClick={() => setDeletingId(a.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="pt-2 border-t">
            <PaginacaoRodape
              pagina={pagina}
              totalPaginas={Math.max(1, totalPaginas)}
              totalItens={total}
              primeiroItem={total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1}
              ultimoItem={Math.min(pagina * tamanhoPagina, total)}
              tamanhoPagina={tamanhoPagina}
              onPaginaChange={setPagina}
              onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
            />
            {isFetching && !isLoading && <p className="mt-1 text-xs text-muted-foreground">(atualizando...)</p>}
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteAtendimento(deletingId); setDeletingId(null); } }}
        title="Excluir Atendimento"
        description="Tem certeza que deseja excluir este atendimento e todo o seu histórico de contatos?"
      />
    </div>
  );
};

export default Crm;
